package dev.lingvo.shared.screenshots.text

import dev.lingvo.shared.TextDirection
import dev.lingvo.shared.screenshots.doc.HyphenationPolicy
import dev.lingvo.shared.screenshots.doc.TextAlign
import dev.lingvo.shared.screenshots.doc.VAlign
import dev.lingvo.shared.screenshots.render.CanvasAlign
import dev.lingvo.shared.screenshots.render.TextMeasurer
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.min

/** Fits logical text deterministically and refuses rather than drawing below the legibility floor. */
object TextFitter : CaptionFitter {
    /** Measurement and segmentation are injected so preview and export share identical decisions. */
    override fun fit(req: FitRequest, measurer: TextMeasurer, segmenter: Segmenter): FitResult {
        val normal = normalise(req)
        val empty = TextLayout(req.spec.floorSize, emptyList(), 0.0, normal.direction,
            normal.lineHeight * req.spec.floorSize, normal.letterSpacingEm * req.spec.floorSize)
        if (normal.text.isBlank()) return FitResult.Empty(empty)
        val segments = segmenter.segments(normal.text, req.locale)
        val cjk = Scripts.rows.getValue(normal.script).breaking == BreakingMode.PHRASE_KINSOKU
        fun pass(parts: List<Segment>): FitResult {
            val engine = LineLayout(req, normal, measurer, parts)
            val a = search(req, normal, engine, measurer, false)
            if (req.spec.hyphenation == HyphenationPolicy.NONE || parts.none { it.breakAfter == Brk.SOFT_HYPHEN }) return a
            val b = search(req, normal, engine, measurer, true)
            return if (a is FitResult.Refused || b is FitResult.Fitted && b.layout.size + 1e-7 >= 1.15 * a.layout.size) b else a
        }
        val phrase = pass(if (cjk) Kinsoku.phrases(segments) else segments)
        return if (cjk && phrase is FitResult.Refused && phrase.reason != Refusal.BOX_TOO_SMALL) {
            pass(Kinsoku.characters(normal.text))
        } else phrase
    }

    private fun search(req: FitRequest, normal: NormalisedText, engine: LineLayout, measurer: TextMeasurer, hyphens: Boolean): FitResult {
        fun fits(run: LineRun, size: Double): Boolean = !run.tooWide && run.lines.size <= req.spec.maxLines &&
            run.lines.size * normal.lineHeight * size <= req.boxH + 1e-7
        val floor = req.spec.floorSize
        fun refused(): FitResult.Refused {
            val run = engine.at(floor, req.boxW, hyphens, real = true)
            val layout = place(req, normal, engine, measurer, run, floor)
            val reason = when {
                req.boxW < floor || req.boxH < floor * normal.lineHeight -> Refusal.BOX_TOO_SMALL
                run.tooWide -> if (Scripts.needsDictionary(normal.script)) Refusal.NO_BREAK_OPPORTUNITY else Refusal.WORD_TOO_WIDE
                run.lines.size > req.spec.maxLines -> Refusal.TOO_MANY_LINES
                else -> Refusal.TOO_TALL
            }
            return FitResult.Refused(layout, reason, max(0.0, (run.lines.maxOfOrNull { it.width } ?: 0.0) - req.boxW),
                max(0.0, layout.blockHeight - req.boxH), max(0, run.lines.size - req.spec.maxLines))
        }
        if (req.boxW < floor || req.boxH < floor * normal.lineHeight) return refused()
        val floorRun = engine.at(floor, req.boxW, hyphens)
        if (!fits(floorRun, floor)) return refused()
        // The quotient can be 2.99999999999997 for a mathematical three-step span.
        val gridMax = floor((req.spec.maxSize - floor) / req.step + 1e-9).toInt()
        fun sizeAt(index: Int): Double = min(req.spec.maxSize, floor + index * req.step)
        var lo = 0
        var hi = gridMax + 1
        while (hi - lo > 1) {
            val mid = lo + (hi - lo) / 2
            val candidate = sizeAt(mid)
            if (fits(engine.at(candidate, req.boxW, hyphens), candidate)) lo = mid else hi = mid
        }
        var gridIndex = lo
        var size = sizeAt(gridIndex)
        var run = engine.at(size, req.boxW, hyphens)
        var verified = false
        for (attempt in 0..6) {
            val actual = run.copy(lines = run.lines.map { line ->
                line.copy(width = measurer.width(line.text, engine.font(size), normal.letterSpacingEm * size))
            }).let { it.copy(tooWide = it.lines.any { line -> line.width > req.boxW + 1e-7 }) }
            if (fits(actual, size)) {
                run = actual
                verified = true
                break
            }
            gridIndex--
            if (gridIndex < 0 || attempt == 6) break
            size = sizeAt(gridIndex)
            run = engine.at(size, req.boxW, hyphens, real = true)
        }
        if (!verified) return refused()
        var balanced = false
        if (req.spec.balance && run.lines.size >= 2) {
            var lo = 0.5 * req.boxW
            var hi = req.boxW
            val count = run.lines.size
            repeat(10) {
                val mid = (lo + hi) / 2
                val candidate = engine.at(size, mid, hyphens, real = true)
                if (fits(candidate, size) && candidate.lines.size == count) {
                    hi = mid
                    run = candidate
                } else lo = mid
            }
            balanced = true
        }
        return FitResult.Fitted(place(req, normal, engine, measurer, run, size), size < req.spec.maxSize,
            run.lines.any { it.hyphenated }, balanced)
    }

    private fun place(req: FitRequest, normal: NormalisedText, engine: LineLayout, measurer: TextMeasurer, run: LineRun, size: Double): TextLayout {
        val advance = normal.lineHeight * size
        val height = advance * run.lines.size
        val offset = when (req.vAlign) { VAlign.TOP -> 0.0; VAlign.MIDDLE -> (req.boxH - height) / 2; VAlign.BOTTOM -> req.boxH - height }
        val align = when (req.align) {
            TextAlign.CENTER -> CanvasAlign.CENTER
            TextAlign.START -> if (normal.direction == TextDirection.RTL) CanvasAlign.RIGHT else CanvasAlign.LEFT
            TextAlign.END -> if (normal.direction == TextDirection.RTL) CanvasAlign.LEFT else CanvasAlign.RIGHT
        }
        val anchor = when (align) { CanvasAlign.LEFT -> 0.0; CanvasAlign.CENTER -> req.boxW / 2; CanvasAlign.RIGHT -> req.boxW }
        val metrics = measurer.verticalMetrics(engine.font(size))
        return TextLayout(size, run.lines.mapIndexed { i, line ->
            LaidLine(line.text, line.width, anchor, offset + i * advance +
                (advance - metrics.ascent - metrics.descent) / 2 + metrics.ascent, align, line.hyphenated)
        }, height, normal.direction, advance, normal.letterSpacingEm * size)
    }
}
